import StaffTimePicker from "@/components/StaffTimePicker";
import {appointmentColours, readableText} from '@/lib/categoryColours';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, AlertTriangle, CheckCircle2, CreditCard, FileText, Star, CalendarDays, Ban, Moon } from "lucide-react";
import { toast } from "sonner";
import { staffApi } from "@/lib/staffApi";
import { ErrorState, Skeleton } from "@/components/BookingShell";
import { formatFullDate, toISODate } from "@/lib/format";
import { useQuery, invalidate as cacheInvalidate } from "@/lib/queryCache";
import { ReadyEarlyButton } from "@/components/ReadyEarlyButton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as DatePicker } from "@/components/ui/calendar";

/* ---------------------------------------------------------------------------
 * Visual + interaction constants
 * ------------------------------------------------------------------------ */
const VIEWS = ["day", "week", "month"];
const TICK_MIN = 15;         // clean visual grid — hour has 4 sections (Fresha-like)
const SNAP_MIN = 5;          // ANY drag/tap snaps to 5-minute precision internally
// Row density is view-aware: day view stays spacious; week view compacts so more
// of the working day is visible at a glance (Feb 2026 UX polish).
const PX_PER_HOUR_BY_VIEW = { day: 120, week: 72, month: 120 };

/* ---------------------------------------------------------------------------
 * Amelia day mapping — VERIFIED against Angela's live weekDayList vs her
 * declared schedule (dayIndex 6 has the single 10:00-16:00 period she calls
 * "Saturday", dayIndex 2 has the 10:30-18:00 window she calls "Tuesday").
 *   1 = Monday, 2 = Tuesday, ..., 6 = Saturday, 7 = Sunday.
 * JS Date.getDay(): 0=Sun..6=Sat -> Amelia dayIndex.
 * ------------------------------------------------------------------------ */
const DAY_NAMES = {
    1: "Monday", 2: "Tuesday", 3: "Wednesday", 4: "Thursday",
    5: "Friday", 6: "Saturday", 7: "Sunday",
};
function dayIndexOfDate(d) { const js = d.getDay(); return js === 0 ? 7 : js; }

/* ---------------------------------------------------------------------------
 * Small helpers
 * ------------------------------------------------------------------------ */
function startOfWeek(d) { const n = new Date(d); const day = (n.getDay() + 6) % 7; n.setDate(n.getDate() - day); n.setHours(0,0,0,0); return n; }
function addDays(d, n) { const c = new Date(d); c.setDate(c.getDate() + n); return c; }
function hhmmToMin(s) { const [h,m] = (s||"00:00").slice(0,5).split(":").map(Number); return h*60 + (m||0); }
function minToHHMM(m) { return `${String(Math.floor(m/60)).padStart(2,"0")}:${String(m%60).padStart(2,"0")}`; }
function apptStartMin(a) { return hhmmToMin((a.bookingStart||"").slice(11,16)); }
function apptEndMin(a)   { return hhmmToMin((a.bookingEnd||"").slice(11,16)) || (apptStartMin(a) + 15); }
function snap5(m) { return Math.round(m / SNAP_MIN) * SNAP_MIN; }

/**
 * Convert a live Amelia weekDayList entry into normalised period/break info.
 *   - periods: sorted [{ start, end, services:number[] }] (services = periodServiceList, [] = "all allowed")
 *   - breaks:  sorted [{ start, end, kind: "break"|"timeout" }] — union of implicit gaps between periods
 *              AND explicit timeOutList entries (Amelia's "lunch"/"break" objects)
 *   - dayStart/dayEnd: earliest period start / latest period end (source-of-truth working window)
 * A day is "closed" when this object has no periods at all.
 */
function normaliseWeekDay(wd) {
    if (!wd) return { periods: [], breaks: [], dayStart: null, dayEnd: null };
    const periods = (wd.periodList || [])
        .filter((p) => (p.startTime || "") < (p.endTime || ""))
        .map((p) => ({
            start: hhmmToMin(p.startTime),
            end: hhmmToMin(p.endTime),
            services: (p.periodServiceList || []).map((s) => (s && typeof s === "object" ? (s.serviceId ?? s.id) : s)).filter(Boolean),
        }))
        .sort((a, b) => a.start - b.start);
    if (!periods.length) return { periods: [], breaks: [], dayStart: null, dayEnd: null };
    const dayStart = periods[0].start;
    const dayEnd = periods[periods.length - 1].end;
    const breaks = [];
    for (let i = 0; i < periods.length - 1; i++) {
        if (periods[i + 1].start > periods[i].end) {
            breaks.push({ start: periods[i].end, end: periods[i + 1].start, kind: "break" });
        }
    }
    (wd.timeOutList || []).forEach((t) => {
        const s = hhmmToMin(t.startTime), e = hhmmToMin(t.endTime);
        if (e > s) breaks.push({ start: s, end: e, kind: "timeout" });
    });
    return { periods, breaks, dayStart, dayEnd };
}

/**
 * Given a normalised day + a target range [startMin, endMin), classify the overlap:
 *   "clear"   — the whole range fits inside a single working period
 *   "break"   — range overlaps a scheduled break (lunch/timeout) but stays inside working window
 *   "closed"  — range extends outside dayStart..dayEnd or the day has no periods
 */
function classifyRange(info, startMin, endMin) {
    if (!info.periods.length) return "closed";
    if (startMin < info.dayStart || endMin > info.dayEnd) return "closed";
    const inSinglePeriod = info.periods.some((p) => startMin >= p.start && endMin <= p.end);
    if (inSinglePeriod) return "clear";
    for (const b of info.breaks) {
        if (startMin < b.end && endMin > b.start) return "break";
    }
    return "closed";
}

/* Given a start time on a specific day, return the containing period (or the closest one) */
function periodContaining(info, startMin) {
    return info.periods.find((p) => startMin >= p.start && startMin < p.end) || null;
}

/* ---------------------------------------------------------------------------
 * Smart date labels for the calendar header.
 *   Day view:
 *     - Today / Tomorrow / Yesterday for immediately-adjacent days
 *     - "7 Aug" for anything else (year appended only if not the current year)
 *   Week view:
 *     - "This week" / "Next week" / "Last week" for adjacent weeks
 *     - "Week of 4 Aug" otherwise
 *   Month view: full month + year (e.g. "August 2026")
 * The sub-label always gives the concrete date/range so the smart label is
 * never ambiguous.
 * ------------------------------------------------------------------------ */
function _startOfDay(d) { const n = new Date(d); n.setHours(0,0,0,0); return n; }
function _diffDays(a, b) {
    return Math.round((_startOfDay(a).getTime() - _startOfDay(b).getTime()) / 86_400_000);
}
function smartCalendarLabel(anchor, view) {
    const today = new Date();
    if (view === "month") {
        return anchor.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
    }
    if (view === "week") {
        const anchorWeek = startOfWeek(anchor).getTime();
        const thisWeek = startOfWeek(today).getTime();
        const diff = Math.round((anchorWeek - thisWeek) / (7 * 86_400_000));
        if (diff === 0) return "This week";
        if (diff === 1) return "Next week";
        if (diff === -1) return "Last week";
        const s = startOfWeek(anchor);
        const sameYear = s.getFullYear() === today.getFullYear();
        return `Week of ${s.toLocaleDateString("en-GB", sameYear
            ? { day: "numeric", month: "short" }
            : { day: "numeric", month: "short", year: "numeric" })}`;
    }
    // day view
    const diff = _diffDays(anchor, today);
    if (diff === 0) return "Today";
    if (diff === 1) return "Tomorrow";
    if (diff === -1) return "Yesterday";
    const sameYear = anchor.getFullYear() === today.getFullYear();
    return anchor.toLocaleDateString("en-GB", sameYear
        ? { day: "numeric", month: "short" }
        : { day: "numeric", month: "short", year: "numeric" });
}
function calendarSubLabel(anchor, view) {
    if (view === "day") {
        return anchor.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    }
    if (view === "week") {
        const s = startOfWeek(anchor);
        const e = addDays(s, 6);
        const sameMonth = s.getMonth() === e.getMonth();
        const sYr = s.getFullYear();
        const eYr = e.getFullYear();
        const sameYear = sYr === eYr;
        const sFmt = s.toLocaleDateString("en-GB", { day: "numeric", month: sameMonth ? undefined : "short" });
        const eFmt = e.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: sameYear ? undefined : "numeric" });
        return `${sFmt} – ${eFmt}${sameYear ? ` · ${sYr}` : ""}`;
    }
    return "";
}

const STATUS_TONE = {
    pending:  "bg-amber-500/15 border-amber-400/50 text-amber-100",
    approved: "bg-emerald-500/15 border-emerald-400/50 text-emerald-100",
    canceled: "bg-red-500/10 border-red-400/40 text-red-200/70 line-through",
    rejected: "bg-red-500/10 border-red-400/40 text-red-200/70",
    "no-show": "bg-white/5 border-white/20 text-white/60",
};

/* ==========================================================================
 * StaffCalendar — top-level page
 * ======================================================================= */
export function StaffCalendar() {
    const menuQuery = useQuery('staff:calendar:colours', () => staffApi.services(), {staleMs:0});
    const [params, setParams] = useSearchParams();
    const nav = useNavigate();
    const [view, setView] = useState(() => VIEWS.includes(params.get("v")) ? params.get("v") : "day");
    const [anchor, setAnchor] = useState(() => {
        const d = params.get("d"); return d ? new Date(`${d}T00:00:00`) : new Date();
    });
    const [confirmState, setConfirmState] = useState(null); // { iso, startMin, endMin, kind }
    const [refreshingSchedule, setRefreshingSchedule] = useState(false);

    useEffect(() => {
        const p = new URLSearchParams(params);
        p.set("v", view); p.set("d", toISODate(anchor));
        setParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, anchor]);

    const range = useMemo(() => {
        if (view === "day")   return { start: toISODate(anchor), end: toISODate(anchor) };
        if (view === "week")  { const s = startOfWeek(anchor); return { start: toISODate(s), end: toISODate(addDays(s, 6)) }; }
        const s = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
        const e = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
        return { start: toISODate(s), end: toISODate(e) };
    }, [view, anchor]);

    // Cached, range-keyed fetches — visible-range-only per the perf sprint.
    // Switching back to a previously viewed day/week/month is instant because
    // that key is already in the cache; fresh windows get a background fetch.
    const apptsQuery = useQuery(
        `staff:calendar:appts:${range.start}:${range.end}`,
        () => staffApi.listAppointments({ start_date: range.start, end_date: range.end }),
        { staleMs: 20_000 },
    );
    const appts = apptsQuery.data?.appointments ?? null;
    const error = apptsQuery.error;
    const load = apptsQuery.refetch;

    const availQuery = useQuery(
        "staff:availability",
        () => staffApi.availability(),
        { staleMs: 120_000 },
    );
    const availability = availQuery.data || (availQuery.error ? { employees: [] } : null);
    const refreshSchedule = async () => {
        setRefreshingSchedule(true);
        try {
            // Invalidation clears entries but does not re-run mounted query effects.
            // Explicitly fetch both datasets after the successful schedule write.
            await Promise.all([availQuery.refetch(), apptsQuery.refetch(), menuQuery.refetch()]);
        } finally { setRefreshingSchedule(false); }
    };

    // Angela's live schedule -> Map<dayIndex, weekDay>
    const employee = availability?.employees?.[0];
    const weekMap = useMemo(() => {
        const m = {};
        (employee?.weekDayList || []).forEach((w) => { m[w.dayIndex] = w; });
        return m;
    }, [employee]);
    const dayInfoByIndex = useMemo(() => {
        const m = {};
        for (const idx of Object.keys(weekMap)) m[idx] = normaliseWeekDay(weekMap[idx]);
        return m;
    }, [weekMap]);

    // Day-off + special-day lookup keyed by ISO date. Amelia typically returns
    // dayOffList entries with { startDate, endDate, repeat, name }. We treat any
    // date falling inside an entry as closed.
    const dayOffDates = useMemo(() => {
        const set = new Set();
        const list = (employee?.dayOffList || []).concat(availability?.daysOff || []);
        list.forEach((d) => {
            const s = (d.startDate || "").slice(0, 10);
            const e = (d.endDate || d.startDate || "").slice(0, 10);
            if (!s) return;
            let cur = new Date(`${s}T00:00:00`);
            const stop = new Date(`${e || s}T00:00:00`);
            while (cur <= stop) { set.add(toISODate(cur)); cur = addDays(cur, 1); }
        });
        return set;
    }, [employee, availability]);

    // Day extent — take min/max across ALL open days' period bounds, clamped 08:00-20:00
    const [minMinute, maxMinute] = useMemo(() => {
        const days = Object.values(dayInfoByIndex).filter((d) => d.periods.length);
        if (!days.length) return [8*60, 20*60];
        const starts = days.map((d) => d.dayStart);
        const ends = days.map((d) => d.dayEnd);
        return [Math.min(...starts, 8*60), Math.max(...ends, 20*60)];
    }, [dayInfoByIndex]);

    const totalMinutes = maxMinute - minMinute;
    const pxPerHour = PX_PER_HOUR_BY_VIEW[view] || 120;
    const pxPerMin = pxPerHour / 60;
    const gridHeight = totalMinutes * pxPerMin;

    // Ref to the scrollable body so "Today" can also scroll to the current
    // time. Declared here (rather than later) so the useEffect below can
    // read `.current` after mount without a use-before-init runtime error.
    const gridScrollRef = useRef(null);
    // Bump this whenever Angela clicks the Today button so an effect can
    // scroll to the current-time position after React has rendered.
    const [scrollToNowSignal, setScrollToNowSignal] = useState(0);

    // Scroll the calendar body so the current time is comfortably in view.
    // Runs on mount, whenever the view unit changes and whenever Angela
    // presses "Today" (via the incrementing signal).
    useEffect(() => {
        if (view === "month") return;
        // Only auto-scroll when the anchor is in the currently visible range —
        // i.e. today is on-screen. Prevents jumpiness when browsing history.
        const anchorIso = toISODate(anchor);
        const todayIso = toISODate(new Date());
        let containsToday = false;
        if (view === "day") containsToday = anchorIso === todayIso;
        else {
            const s = startOfWeek(anchor);
            for (let i = 0; i < 7; i++) if (toISODate(addDays(s, i)) === todayIso) { containsToday = true; break; }
        }
        if (!containsToday) return;
        const el = gridScrollRef.current;
        if (!el) return;
        // Small delay so the grid has laid out.
        const raf = requestAnimationFrame(() => {
            const now = new Date();
            const nowM = now.getHours() * 60 + now.getMinutes();
            const y = Math.max(0, (nowM - minMinute) * pxPerMin - el.clientHeight * 0.35);
            el.scrollTo({ top: y, behavior: "smooth" });
        });
        return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, scrollToNowSignal]);

    const byDay = useMemo(() => {
        const g = {};
        (appts || []).forEach((a) => { const k = (a.bookingStart||"").slice(0,10); (g[k] ||= []).push({...a, calendarColours:appointmentColours(a, menuQuery.data)}); });
        return g;
    }, [appts, menuQuery.data]);

    // Returning customers = customer ids appearing in 2+ appointments across
    // the currently loaded window. Fast, no extra API calls.
    const returningCustomerIds = useMemo(() => {
        const counts = new Map();
        (appts || []).forEach((a) => {
            const cid = a.customerId;
            if (!cid) return;
            counts.set(cid, (counts.get(cid) || 0) + 1);
        });
        return new Set(Array.from(counts.entries()).filter(([_, n]) => n > 1).map(([id]) => id));
    }, [appts]);

    // Forms-completed appointments — fetched cheaply from the packages endpoint.
    const [formsCompletedApptIds, setFormsCompletedApptIds] = useState(new Set());
    useEffect(() => {
        let cancelled = false;
        staffApi.formsPackages?.({ status: "complete" })
            .then((r) => {
                if (cancelled) return;
                const ids = new Set((r?.packages || []).map((p) => p.appointment_id).filter(Boolean));
                setFormsCompletedApptIds(ids);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [appts]);

    const goPrev = () => setAnchor(view === "day" ? addDays(anchor, -1) : view === "week" ? addDays(anchor, -7) : new Date(anchor.getFullYear(), anchor.getMonth()-1, 1));
    const goNext = () => setAnchor(view === "day" ? addDays(anchor, 1)  : view === "week" ? addDays(anchor, 7)  : new Date(anchor.getFullYear(), anchor.getMonth()+1, 1));

    // Switching between Day / Week / Month behaves like Google Calendar / Outlook:
    // the view always snaps to *today's* context so the picker never has to open.
    const switchView = (v) => {
        setView(v);
        setAnchor(new Date());
    };

    const jumpToToday = () => {
        setAnchor(new Date());
        setScrollToNowSignal((n) => n + 1);
    };

    // Explicit popover for jumping to a date. Only rendered when the user clicks
    // the calendar icon — the Day/Week/Month toggle never opens it.
    const [pickerOpen, setPickerOpen] = useState(false);
    const handlePickDate = (d) => {
        if (!d) return;
        setAnchor(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
        setPickerOpen(false);
    };

    // Block-time modal (Availability Editor quick action).
    const [blockOpen, setBlockOpen] = useState(false);
    // When drag-select on empty time chooses Block instead of Book, prefill the
    // modal with the selected date + start/end.
    const [blockPrefill, setBlockPrefill] = useState(null); // { iso, startMin, endMin }
    // "Choose action" chooser modal after drag-selecting an empty range.
    const [rangeChoice, setRangeChoice] = useState(null); // { iso, startMin, endMin }
    const employeeId = employee?.id;

    // Drag-to-reschedule handoff (kept together with the calendar state so the
    // TimeGrid can call up with the new time).
    const [rescheduleState, setRescheduleState] = useState(null); // { appt, newIso, newStartMin }
    const openReschedule = (appt, newIso, newStartMin) => {
        if (appt.calendarBreak) {
            if (newIso !== appt.bookingStart.slice(0, 10)) {
                toast.error("Move breaks within the same day. Use Availability to change days.");
                return;
            }
            setBlockPrefill({ iso: newIso, startMin: newStartMin,
                endMin: newStartMin + apptEndMin(appt) - apptStartMin(appt),
                originalStart: appt.bookingStart.slice(11, 16), originalEnd: appt.bookingEnd.slice(11, 16) });
            setBlockOpen(true);
            return;
        }
        // Same time as current — no-op.
        const currentIso = (appt.bookingStart || "").slice(0, 10);
        const currentMin = apptStartMin(appt);
        if (currentIso === newIso && currentMin === newStartMin) return;
        setRescheduleState({ appt, newIso, newStartMin });
    };
    const cancelReschedule = () => setRescheduleState(null);
    const confirmReschedule = async () => {
        if (!rescheduleState) return;
        const { appt, newIso, newStartMin } = rescheduleState;
        try {
            await staffApi.reschedule(appt.id, newIso, minToHHMM(newStartMin));
            toast.success(`Rescheduled ${appt.customer?.firstName || "appointment"} — customer notified.`);
            cacheInvalidate("staff:calendar");
            load();
            setRescheduleState(null);
        } catch (e) {
            // Keep the modal open so Angela can retry without re-dragging.
            toast.error(e?.message || "Reschedule failed");
        }
    };

    const infoForDate = (d) => {
        if (dayOffDates.has(toISODate(d))) return { periods: [], breaks: [], dayStart: null, dayEnd: null, forcedClosed: true };
        const iso = toISODate(d);
        const special = (employee?.specialDayList || []).filter((s) =>
            String(s.startDate).slice(0, 10) <= iso && String(s.endDate).slice(0, 10) >= iso);
        if (special.length) return normaliseWeekDay(special[0]);
        return dayInfoByIndex[dayIndexOfDate(d)] || { periods: [], breaks: [], dayStart: null, dayEnd: null };
    };

    const openBooking = (iso, startMin, endMin, override = false) => {
        // Amelia's slot step is a per-business setting (currently 5 minutes) —
        // we forward the drag's exact start time and let the ManualBooking step
        // reconcile against Amelia's returned availability, which is the only
        // source of truth. No client-side snapping.
        const params = new URLSearchParams();
        params.set("date", iso);
        params.set("time", minToHHMM(startMin));
        if (endMin > startMin) params.set("duration", String(endMin - startMin));
        const info = infoForDate(new Date(`${iso}T00:00:00`));
        const period = periodContaining(info, startMin);
        if (period && period.services.length) params.set("services", period.services.join(","));
        if (override) params.set("override", "1");
        nav(`/staff/booking/new?${params.toString()}`);
    };

    const handleSelectRange = (iso, startMin, endMin) => {
        const info = infoForDate(new Date(`${iso}T00:00:00`));
        const kind = classifyRange(info, startMin, endMin);
        if (kind === "clear") {
            // Empty available time → show the Book / Block chooser so Angela can
            // intentionally decide (per Feb-2026 UX polish: "do not create a
            // booking when the user is intentionally blocking time").
            setRangeChoice({ iso, startMin, endMin });
            return;
        }
        setConfirmState({ iso, startMin, endMin, kind });
    };

    const dismissConfirm = () => setConfirmState(null);
    const acceptConfirm = () => {
        const { iso, startMin, endMin } = confirmState;
        setConfirmState(null);
        openBooking(iso, startMin, endMin, true);
    };

    return (
        <div className="-mx-4 sm:-mx-6 md:mx-0 -mt-6 md:mt-0 -mb-[calc(env(safe-area-inset-bottom)+96px)] md:mb-0 flex flex-col h-[calc(100dvh-env(safe-area-inset-top,0px)-33px)] md:h-auto" data-testid="staff-calendar">
            <div
                className="shrink-0 px-4 sm:px-6 md:px-0 py-1.5 md:py-3 glass-strong md:glass-strong md:bg-transparent md:backdrop-blur-0 md:border-0 md:py-0"
                style={{ paddingTop: "max(env(safe-area-inset-top, 0px), 14px)" }}
            >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <h1 className="font-serif font-light text-2xl md:text-4xl text-white truncate" data-testid="cal-title">
                            {smartCalendarLabel(anchor, view)}
                        </h1>
                        <p className="text-[11px] uppercase tracking-[0.18em] text-white/55 mt-1" data-testid="cal-sublabel">
                            {calendarSubLabel(anchor, view)}
                        </p>
                    </div>
                    <div className="flex items-center gap-1 md:gap-1.5 flex-wrap justify-end">
                        <button onClick={goPrev} data-testid="cal-prev"
                                aria-label="Previous"
                                className="h-9 w-9 md:h-10 md:w-10 rounded-full border border-white/40 text-white font-semibold hover:border-gold hover:text-gold flex items-center justify-center transition-colors">
                            <ChevronLeft className="h-4 w-4" strokeWidth={2.5} />
                        </button>
                        <button onClick={jumpToToday} data-testid="cal-today"
                                className="rounded-full border border-white/40 text-[11px] md:text-xs font-semibold text-white hover:border-gold hover:text-gold px-3 md:px-4 h-9 md:h-10 transition-colors">
                            Today
                        </button>
                        <button onClick={goNext} data-testid="cal-next"
                                aria-label="Next"
                                className="h-9 w-9 md:h-10 md:w-10 rounded-full border border-white/40 text-white font-semibold hover:border-gold hover:text-gold flex items-center justify-center transition-colors">
                            <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
                        </button>
                        {/* Popover date picker — the ONLY control that opens the date picker.
                            Switching Day / Week / Month never triggers it (see switchView). */}
                        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                            <PopoverTrigger asChild>
                                <button type="button"
                                        aria-label="Jump to date"
                                        data-testid="cal-date-picker"
                                        className="h-9 w-9 md:h-10 md:w-10 rounded-full border border-white/40 text-white font-semibold hover:border-gold hover:text-gold flex items-center justify-center transition-colors">
                                    <CalendarDays className="h-4 w-4" strokeWidth={2.25} />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="app-light w-auto p-3 bg-white border border-navy/15 shadow-lg rounded-xl text-navy" data-testid="cal-date-picker-popover">
                                <DatePicker
                                    mode="single"
                                    selected={anchor}
                                    onSelect={handlePickDate}
                                    defaultMonth={anchor}
                                    initialFocus
                                    showOutsideDays={true}
                                    // No `disabled` prop — the picker is for navigation, not
                                    // booking availability, so closed salon days remain
                                    // selectable per the Feb-2026 UX brief.
                                />
                            </PopoverContent>
                        </Popover>
                        <div className="ml-1 flex rounded-full border border-white/40 overflow-hidden">
                            {VIEWS.map((v) => (
                                <button key={v} onClick={() => switchView(v)} data-testid={`cal-view-${v}`}
                                        aria-label={v}
                                        className={`px-2.5 md:px-4 h-9 md:h-10 text-[11px] md:text-xs font-semibold capitalize transition-colors ${
                                            view === v
                                                ? "bg-gold text-navy"
                                                : "text-white hover:bg-white/10"
                                        }`}>
                                    {v}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Only replace the grid with an error screen if we have no cached data yet. */}
            {refreshingSchedule && <div role="status" className="flex items-center justify-center gap-3 p-8 text-navy" data-testid="calendar-refresh-status"><span aria-hidden="true" className="h-6 w-6 animate-spin rounded-full border-2 border-gold border-t-transparent" />Updating calendar…</div>}
            {!refreshingSchedule && availQuery.error && <div role="alert" className="p-4 text-navy">The schedule could not be refreshed. Your saved change has not been undone. <button type="button" className="underline" onClick={refreshSchedule}>Retry refresh</button></div>}
            {error && !appts && <ErrorState error={error} onRetry={load} />}
            {error && appts && (
                <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-100 flex items-center justify-between" data-testid="calendar-stale-banner">
                    <span>Connection is slow right now. Showing the last known schedule.</span>
                    <button type="button" onClick={load} className="underline">Retry</button>
                </div>
            )}
            {(!appts || !availability) && !error && <Skeleton className="h-96" />}

            {appts && availability && view === "day" && (
                <TimeGrid
                    dates={[anchor]}
                    infoForDate={infoForDate}
                    minMinute={minMinute}
                    totalMinutes={totalMinutes}
                    gridHeight={gridHeight}
                    pxPerMin={pxPerMin}
                    byDay={byDay}
                    onSelectRange={handleSelectRange}
                    onRescheduleAppt={openReschedule}
                    returningCustomerIds={returningCustomerIds}
                    formsCompletedApptIds={formsCompletedApptIds}
                    scrollRef={gridScrollRef}
                    testId="day-grid"
                />
            )}
            {appts && availability && view === "week" && (
                <TimeGrid
                    dates={Array.from({length:7}, (_, i) => addDays(startOfWeek(anchor), i))}
                    infoForDate={infoForDate}
                    minMinute={minMinute}
                    totalMinutes={totalMinutes}
                    gridHeight={gridHeight}
                    pxPerMin={pxPerMin}
                    byDay={byDay}
                    onSelectRange={handleSelectRange}
                    onRescheduleAppt={openReschedule}
                    returningCustomerIds={returningCustomerIds}
                    formsCompletedApptIds={formsCompletedApptIds}
                    scrollRef={gridScrollRef}
                    testId="week-grid"
                />
            )}
            {appts && availability && view === "month" && (
                <MonthView
                    anchor={anchor}
                    byDay={byDay}
                    infoForDate={infoForDate}
                    onPickDay={(d) => { setAnchor(new Date(`${d}T00:00:00`)); setView("day"); }}
                />
            )}

            {confirmState && (
                <ConfirmBreakDialog
                    state={confirmState}
                    onCancel={dismissConfirm}
                    onConfirm={acceptConfirm}
                />
            )}

            {rangeChoice && (
                <RangeChoiceModal
                    state={rangeChoice}
                    onCancel={() => setRangeChoice(null)}
                    onBook={() => {
                        const r = rangeChoice; setRangeChoice(null);
                        openBooking(r.iso, r.startMin, r.endMin, false);
                    }}
                    onBlock={() => {
                        const r = rangeChoice; setRangeChoice(null);
                        setBlockPrefill(r);
                        setBlockOpen(true);
                    }}
                />
            )}

            {rescheduleState && (
                <RescheduleConfirmDialog
                    state={rescheduleState}
                    onCancel={cancelReschedule}
                    onConfirm={confirmReschedule}
                />
            )}

            {blockOpen && (
                <BlockTimeModal
                    anchor={anchor}
                    initialRange={blockPrefill}
                    employeeId={employeeId}
                    employee={employee}
                    infoForDate={infoForDate}
                    onClose={() => { setBlockOpen(false); setBlockPrefill(null); }}
                    onSaved={() => {
                        setBlockOpen(false);
                        setBlockPrefill(null);
                        cacheInvalidate("staff:availability");
                        cacheInvalidate("staff:calendar");
                        refreshSchedule();
                        toast.success("Time blocked — the schedule has been updated.");
                    }}
                />
            )}

            {/* Floating Block-Time action — visible in every view, sits above the
                mobile bottom nav so it never obscures appointments. Slightly
                smaller (~10%) per the Feb-2026 mockup, seated deeper into the
                bottom-right corner with safe-area padding. */}
            <button
                type="button"
                data-testid="cal-block-fab"
                aria-label="Block time"
                title="Block time"
                onClick={() => { setBlockPrefill(null); setBlockOpen(true); }}
                className="fixed z-30 h-12 w-12 rounded-full bg-gold text-navy shadow-[0_8px_22px_rgba(15,26,61,0.25)] flex items-center justify-center hover:brightness-110 active:scale-95 transition-transform"
                style={{
                    right: "max(env(safe-area-inset-right), 1rem)",
                    bottom: "max(env(safe-area-inset-bottom), 5.5rem)",
                }}
            >
                <Ban className="h-4 w-4" strokeWidth={2.5} />
            </button>
        </div>
    );
}

/* ==========================================================================
 * TimeGrid — press-hold-drag to select any 5-minute range
 * ======================================================================= */
function TimeGrid({ dates, infoForDate, minMinute, totalMinutes, gridHeight, byDay, onSelectRange, onRescheduleAppt, returningCustomerIds, formsCompletedApptIds, pxPerMin, scrollRef, testId }) {
    const PX_PER_MIN = pxPerMin;
    // Column minimum width — day view stays spacious; week view goes narrow so
    // 7 columns fit at 430 px and are close-to-fitting at 390 px (still smooth
    // horizontal scroll on 360 px). The grid uses `minmax(N,1fr)` so wider
    // viewports expand columns without extra effort.
    const columnMinPx = dates.length > 1 ? 56 : 120;
    // Track which column is a valid drop-target during a drag so we can paint
    // a subtle highlight ring around it.
    const [dropHover, setDropHover] = useState(null); // { iso, min }
    const hoursMarks = [];
    for (let m = Math.floor(minMinute/60)*60; m <= minMinute + totalMinutes; m += 60) hoursMarks.push(m);
    // 15-min tick lines. We differentiate :30 (medium) from :15 and :45 (light)
    // so Angela can read start/end times without counting rows.
    const halfHourMarks = [];
    const quarterMarks = [];
    for (let m = Math.ceil(minMinute / TICK_MIN) * TICK_MIN; m <= minMinute + totalMinutes; m += TICK_MIN) {
        if (m % 60 === 0) continue;
        if (m % 30 === 0) halfHourMarks.push(m);
        else quarterMarks.push(m);
    }

    const columnRefs = useRef({});
    const [drag, setDrag] = useState(null); // { iso, startMin, endMin, anchorMin }
    // Everything below is driven by refs so touch-event listeners (which are
    // attached natively with `{ passive: false }` on each column) can read the
    // latest state without stale-closure issues.
    const dragRef = useRef(null);
    useEffect(() => { dragRef.current = drag; }, [drag]);
    const onSelectRangeRef = useRef(onSelectRange);
    useEffect(() => { onSelectRangeRef.current = onSelectRange; }, [onSelectRange]);
    const minMinuteRef = useRef(minMinute);
    const totalMinutesRef = useRef(totalMinutes);
    useEffect(() => { minMinuteRef.current = minMinute; totalMinutesRef.current = totalMinutes; }, [minMinute, totalMinutes]);

    // Gesture state machine for TOUCH input.
    //   idle          -> nothing pending
    //   pending       -> finger down on empty grid, waiting on long-press timer
    //   selecting     -> long-press fired, user is now sculpting a range
    //   appt-pending  -> finger down on an appointment block, waiting on long-press
    //   appt-dragging -> long-press fired on an appt — Angela is now dragging it
    // We NEVER navigate from pointerdown / timer / pointermove — only from touchend.
    const gestureRef = useRef({ state: "idle", iso: null, anchorMin: 0, startX: 0, startY: 0, timer: null, appt: null });
    const dropHoverRef = useRef(null);
    // Touch handlers must see the last position even before React renders.
    const updateTouchDrop = (next) => {
        dropHoverRef.current = next;
        setDropHover(next);
    };
    const suppressedClickRef = useRef(null);
    const onRescheduleApptRef = useRef(onRescheduleAppt);
    useEffect(() => { onRescheduleApptRef.current = onRescheduleAppt; }, [onRescheduleAppt]);
    const LONG_PRESS_MS = 450;
    const MOVE_TOLERANCE_PX = 8;

    // Keep mobile moves on the original day and preserve the grab offset.
    const updateAppointmentDrop = (clientY) => {
        const g = gestureRef.current;
        const col = columnRefs.current[g.iso];
        if (!col || !g.appt) return;
        const duration = Math.max(apptEndMin(g.appt) - apptStartMin(g.appt), 15);
        const raw = minMinuteRef.current +
            (clientY - col.getBoundingClientRect().top) / PX_PER_MIN - g.grabOffsetMin;
        const latest = Math.floor(Math.min(1440 - duration,
            minMinuteRef.current + totalMinutesRef.current - duration) / SNAP_MIN) * SNAP_MIN;
        const min = Math.max(minMinuteRef.current, Math.min(latest, snap5(raw)));
        updateTouchDrop({ iso: g.iso, min, apptId: g.appt.id });
    };
    const suppressAppointmentClick = (appt) => {
        if (appt) suppressedClickRef.current = { id: appt.id, until: Date.now() + 800 };
    };

    const cancelGesture = () => {
        const g = gestureRef.current;
        if (g.timer) { clearTimeout(g.timer); g.timer = null; }
        if (g.state === "appt-dragging") suppressAppointmentClick(g.appt);
        g.state = "idle"; g.iso = null; g.appt = null;
        dragRef.current = null;
        setDrag(null);
        updateTouchDrop(null);
    };
    useEffect(() => () => cancelGesture(), []);

    // Current time in Europe/London — never trust the browser's local clock
    // (staff may be travelling or on a laptop set to another tz).
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((n) => n + 1), 60_000);
        return () => clearInterval(id);
    }, []);
    const { londonToday, londonMin } = useMemo(() => {
        try {
            const fmt = new Intl.DateTimeFormat("en-GB", {
                timeZone: "Europe/London",
                year: "numeric", month: "2-digit", day: "2-digit",
                hour: "2-digit", minute: "2-digit", hour12: false,
            });
            const parts = Object.fromEntries(fmt.formatToParts(new Date()).map((p) => [p.type, p.value]));
            const iso = `${parts.year}-${parts.month}-${parts.day}`;
            // At midnight the hour part can be "24" in some ICU versions.
            const h = parts.hour === "24" ? 0 : parseInt(parts.hour, 10);
            const m = parseInt(parts.minute, 10);
            return { londonToday: iso, londonMin: h * 60 + m };
        } catch (_) {
            const n = new Date();
            return { londonToday: toISODate(n), londonMin: n.getHours() * 60 + n.getMinutes() };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tick]);
    const nowMin = londonMin;
    const showNowLine = (dateISO) => dateISO === londonToday && nowMin >= minMinute && nowMin <= minMinute + totalMinutes;
    const isPastOnDate = (dateISO, minute) => dateISO === londonToday && minute < nowMin;

    const clientYToMin = (colEl, clientY) => {
        const rect = colEl.getBoundingClientRect();
        const offset = clientY - rect.top;
        const raw = minMinuteRef.current + offset / PX_PER_MIN;
        return Math.max(minMinuteRef.current, Math.min(minMinuteRef.current + totalMinutesRef.current, snap5(raw)));
    };

    /* ---------- Mouse / stylus: immediate drag via pointer events -------- */
    const onPointerDown = (iso, e) => {
        if (e.pointerType === "touch") return; // touch handled by native listeners
        if (e.target.closest("[data-appt-block]")) return;
        if (e.button && e.button !== 0) return;
        const col = columnRefs.current[iso];
        if (!col) return;
        const m = clientYToMin(col, e.clientY);
        // Refuse to start a selection in a past slot on today.
        if (isPastOnDate(iso, m)) return;
        const next = { iso, startMin: m, endMin: m + SNAP_MIN, anchorMin: m };
        dragRef.current = next;
        setDrag(next);
        try { col.setPointerCapture(e.pointerId); } catch (_) {}
        e.preventDefault();
    };
    const onPointerMove = (e) => {
        if (e.pointerType === "touch") return;
        const active = dragRef.current;
        if (!active) return;
        const col = columnRefs.current[active.iso];
        if (!col) return;
        const m = clientYToMin(col, e.clientY);
        const a = active.anchorMin;
        const start = Math.min(a, m);
        const end = Math.max(a, m);
        const next = { ...active, startMin: start, endMin: end === start ? start + SNAP_MIN : end };
        dragRef.current = next;
        setDrag(next);
    };
    const onPointerUp = (e) => {
        if (e.pointerType === "touch") return;
        const d = dragRef.current;
        if (!d) return;
        dragRef.current = null;
        setDrag(null);
        const end = d.endMin <= d.startMin ? d.startMin + SNAP_MIN : d.endMin;
        onSelectRangeRef.current(d.iso, d.startMin, end);
        e.preventDefault();
    };

    /* ---------- Touch: native listeners with { passive: false } ---------- */
    // Attaching listeners natively is essential because React's synthetic
    // touchmove is passive-by-default. We MUST be able to preventDefault while
    // sculpting a selection or Chromium will (a) scroll the page and (b) fire
    // pointercancel — which was causing premature "book on activation" in the
    // previous implementation.
    useEffect(() => {
        const disposers = [];
        for (const d of dates) {
            const iso = toISODate(d);
            const el = columnRefs.current[iso];
            if (!el) continue;

            const onTouchStart = (evt) => {
                if (evt.touches.length > 1) { cancelGesture(); return; }
                const g = gestureRef.current;
                if (g.state !== "idle") return;
                const t = evt.touches[0];
                if (evt.target.closest?.("button, input, select, textarea")) return;
                const apptEl = evt.target.closest && evt.target.closest("[data-appt-block]");
                if (apptEl) {
                    // Long-press-drag an appointment (mobile reschedule).
                    if (!onRescheduleApptRef.current || apptEl.getAttribute("data-appt-movable") !== "true") return;
                    let payload = null;
                    try { payload = JSON.parse(apptEl.getAttribute("data-appt-payload") || "null"); }
                    catch (_) { payload = null; }
                    if (!payload) return;
                    g.state = "appt-pending";
                    g.iso = iso;
                    g.appt = payload;
                    g.grabOffsetMin = minMinuteRef.current +
                        (t.clientY - el.getBoundingClientRect().top) / PX_PER_MIN - apptStartMin(payload);
                    g.startX = t.clientX;
                    g.startY = t.clientY;
                    g.timer = setTimeout(() => {
                        g.timer = null;
                        if (g.state !== "appt-pending") return;
                        g.state = "appt-dragging";
                        // Seed the drop indicator at the appt's current start time so
                        // Angela sees the "grabbed" marker as soon as the long-press fires.
                        const startMin = apptStartMin(g.appt);
                        updateTouchDrop({ iso: g.iso, min: startMin, apptId: g.appt.id });
                        if (typeof navigator !== "undefined" && navigator.vibrate) { try { navigator.vibrate(20); } catch (_) {} }
                    }, LONG_PRESS_MS);
                    return;
                }
                const m = clientYToMin(el, t.clientY);
                // Reject long-press start on past slots for today.
                if (isPastOnDate(iso, m)) return;
                g.state = "pending";
                g.iso = iso;
                g.anchorMin = m;
                g.startX = t.clientX;
                g.startY = t.clientY;
                g.timer = setTimeout(() => {
                    g.timer = null;
                    if (g.state !== "pending") return;
                    g.state = "selecting";
                    const initial = { iso: g.iso, startMin: g.anchorMin, endMin: g.anchorMin + SNAP_MIN, anchorMin: g.anchorMin };
                    dragRef.current = initial;
                    setDrag(initial);
                    if (typeof navigator !== "undefined" && navigator.vibrate) { try { navigator.vibrate(20); } catch (_) {} }
                }, LONG_PRESS_MS);
            };

            const onTouchMove = (evt) => {
                const g = gestureRef.current;
                if (evt.touches.length !== 1) { cancelGesture(); return; }
                const t = evt.touches[0]; if (!t) return;
                if (g.state === "appt-dragging") {
                    // Before activation swipes scroll normally; afterwards we own the move.
                    if (!evt.cancelable) { cancelGesture(); return; }
                    evt.preventDefault();
                    updateAppointmentDrop(t.clientY);
                    return;
                }
                if (g.state === "appt-pending") {
                    const dx = Math.abs(t.clientX - g.startX);
                    const dy = Math.abs(t.clientY - g.startY);
                    if (dx > MOVE_TOLERANCE_PX || dy > MOVE_TOLERANCE_PX) {
                        // Angela is scrolling — abort the pending long-press.
                        if (g.timer) { clearTimeout(g.timer); g.timer = null; }
                        g.state = "idle"; g.iso = null; g.appt = null;
                    }
                    return;
                }
                if (g.state === "selecting") {
                    // We own the gesture — block page scroll and update selection.
                    evt.preventDefault();
                    const m = clientYToMin(el, t.clientY);
                    const a = g.anchorMin;
                    const start = Math.min(a, m);
                    const end = Math.max(a, m);
                    const next = { iso: g.iso, anchorMin: a, startMin: start, endMin: end === start ? start + SNAP_MIN : end };
                    dragRef.current = next;
                    setDrag(next);
                    return;
                }
                if (g.state === "pending") {
                    const dx = Math.abs(t.clientX - g.startX);
                    const dy = Math.abs(t.clientY - g.startY);
                    if (dx > MOVE_TOLERANCE_PX || dy > MOVE_TOLERANCE_PX) {
                        // User is scrolling — abort silently and let the browser take over.
                        if (g.timer) { clearTimeout(g.timer); g.timer = null; }
                        g.state = "idle"; g.iso = null;
                    }
                }
            };

            const onTouchEnd = (evt) => {
                const g = gestureRef.current;
                if (g.state === "appt-dragging") {
                    // Fire reschedule with the last-known drop target and swallow
                    // the click event that would otherwise bubble to the Link.
                    const released = evt.changedTouches[0];
                    if (released) updateAppointmentDrop(released.clientY);
                    const drop = dropHoverRef.current;
                    const appt = g.appt;
                    g.state = "idle"; g.iso = null; g.appt = null;
                    if (g.timer) { clearTimeout(g.timer); g.timer = null; }
                    updateTouchDrop(null);
                    suppressAppointmentClick(appt);
                    if (drop && appt) {
                        // Preventing default here suppresses the "compat" click
                        // that most touch browsers fire after touchend.
                        if (evt.cancelable) evt.preventDefault();
                        if (drop.iso !== (appt.bookingStart || "").slice(0, 10) || drop.min !== apptStartMin(appt)) {
                            onRescheduleApptRef.current?.(appt, drop.iso, drop.min);
                        }
                    }
                    return;
                }
                if (g.state === "appt-pending") {
                    // Short tap on an appt — let the Link handle navigation.
                    if (g.timer) { clearTimeout(g.timer); g.timer = null; }
                    g.state = "idle"; g.iso = null; g.appt = null;
                    return;
                }
                if (g.state === "selecting") {
                    const d = dragRef.current;
                    g.state = "idle"; g.iso = null;
                    if (g.timer) { clearTimeout(g.timer); g.timer = null; }
                    dragRef.current = null;
                    setDrag(null);
                    if (d) {
                        const end = d.endMin <= d.startMin ? d.startMin + SNAP_MIN : d.endMin;
                        onSelectRangeRef.current(d.iso, d.startMin, end);
                    }
                    return;
                }
                // Pending → tap → no-op. Idle → also no-op.
                if (g.timer) { clearTimeout(g.timer); g.timer = null; }
                g.state = "idle"; g.iso = null;
            };

            const onTouchCancel = () => {
                // Abort quietly — the browser or OS reclaimed the gesture.
                // Because we preventDefault in touchmove while selecting, this
                // essentially only fires from OS interruption (call, notif, etc.).
                cancelGesture();
            };

            el.addEventListener("touchstart", onTouchStart, { passive: true });
            el.addEventListener("touchmove", onTouchMove, { passive: false });
            el.addEventListener("touchend", onTouchEnd, { passive: false });
            el.addEventListener("touchcancel", onTouchCancel, { passive: true });
            disposers.push(() => {
                el.removeEventListener("touchstart", onTouchStart);
                el.removeEventListener("touchmove", onTouchMove);
                el.removeEventListener("touchend", onTouchEnd);
                el.removeEventListener("touchcancel", onTouchCancel);
            });
        }
        return () => { disposers.forEach((f) => f()); cancelGesture(); };
        // Re-attach when the set of visible dates changes (day/week switch,
        // navigation). We stringify dates so identity of Date objects doesn't
        // spuriously retrigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dates.map(toISODate).join(","), PX_PER_MIN, londonToday, nowMin]);

    return (
        <div className="overflow-hidden bg-white flex-1 min-h-0 flex flex-col md:rounded-2xl md:border md:hairline md:shadow-[0_2px_10px_rgba(15,26,61,0.06)]" data-testid={testId}>
            {/* Single horizontally-scrollable container so the day header stays aligned
                with the day columns while the user swipes left/right. */}
            <div
                ref={scrollRef}
                className="flex-1 min-h-0 overflow-x-auto overflow-y-auto"
                style={{
                    WebkitOverflowScrolling: "touch",
                    touchAction: "pan-x pan-y",
                }}
                data-testid={`${testId}-scroll`}
            >
                <div style={{ minWidth: dates.length > 1 ? `${44 + dates.length * columnMinPx}px` : undefined }}>
                    {/* Header row */}
                    <div className="grid bg-white sticky top-0 z-20" style={{ gridTemplateColumns: `44px repeat(${dates.length}, minmax(${columnMinPx}px,1fr))` }}>
                        <div className="border-b border-navy/10" />
                        {dates.map((d) => {
                            const isToday = toISODate(d) === toISODate(new Date());
                            return (
                                <div key={d.toISOString()} className={`text-center py-3 border-l border-b border-navy/10 text-xs ${isToday ? "text-gold" : "text-navy/60"}`}>
                                    <div className="uppercase tracking-[0.16em] text-[10px] font-semibold">{d.toLocaleDateString("en-GB",{weekday:"short"})}</div>
                                    <div className={`font-serif ${isToday ? "text-gold" : "text-navy"} text-lg`}>{d.getDate()}</div>
                                </div>
                            );
                        })}
                    </div>
                    {/* Body */}
                    <div className="grid" style={{ gridTemplateColumns: `44px repeat(${dates.length}, minmax(${columnMinPx}px,1fr))` }}>
                        {/* Time gutter — navy on white to match the mockup. */}
                        <div className="relative bg-white border-r border-navy/10" style={{ height: gridHeight }}>
                            {hoursMarks.map((m) => (
                                <div key={m} className="absolute right-2 text-[10.5px] font-semibold text-navy/55 tabular-nums"
                                     style={{ top: (m - minMinute) * PX_PER_MIN - 6 }}>
                                    {String(Math.floor(m / 60)).padStart(2, "0")}:00
                                </div>
                            ))}
                            {/* Live current-time chip on the gutter (only when today is visible) */}
                            {dates.some((d) => toISODate(d) === toISODate(new Date())) && nowMin >= minMinute && nowMin <= minMinute + totalMinutes && (
                                <div className="absolute right-1 text-[10.5px] font-bold text-gold tabular-nums bg-white px-1 rounded"
                                     style={{ top: (nowMin - minMinute) * PX_PER_MIN - 7 }}
                                     data-testid="now-chip">
                                    {minToHHMM(nowMin)}
                                </div>
                            )}
                        </div>
                {dates.map((d) => {
                    const iso = toISODate(d);
                    const info = infoForDate(d);
                    const items = (byDay[iso] || []).filter((a) => !["canceled","cancelled","rejected","no-show","noshow","no_show"].includes((a.status || "").toLowerCase()));

                    return (
                        <div
                            key={iso}
                            ref={(el) => { columnRefs.current[iso] = el; }}
                            onPointerDown={(e) => onPointerDown(iso, e)}
                            onPointerMove={onPointerMove}
                            onPointerUp={onPointerUp}
                            onDragOver={(e) => {
                                if (!onRescheduleAppt) return;
                                // Column accepts the drop; snap the highlight to 5-minute precision.
                                e.preventDefault();
                                e.dataTransfer.dropEffect = "move";
                                const el = columnRefs.current[iso];
                                if (!el) return;
                                const m = clientYToMin(el, e.clientY);
                                setDropHover((prev) => (prev && prev.iso === iso && prev.min === m ? prev : { iso, min: m }));
                            }}
                            onDragLeave={() => {
                                setDropHover((prev) => (prev && prev.iso === iso ? null : prev));
                            }}
                            onDrop={(e) => {
                                if (!onRescheduleAppt) return;
                                e.preventDefault();
                                setDropHover(null);
                                const el = columnRefs.current[iso];
                                if (!el) return;
                                const m = clientYToMin(el, e.clientY);
                                const payload = e.dataTransfer.getData("application/json");
                                if (!payload) return;
                                try {
                                    const appt = JSON.parse(payload);
                                    onRescheduleAppt(appt, iso, m);
                                } catch (_) { /* invalid payload */ }
                            }}
                            className={`relative border-l border-navy/10 select-none bg-white ${dropHover && dropHover.iso === iso ? "ring-2 ring-gold/50" : ""}`}
                            style={{ height: gridHeight, touchAction: "pan-x pan-y" }}
                            data-testid={`col-${iso}`}
                        >
                            {/* Base grid — visible on the WHITE canvas (Feb 2026 mockup).
                                Quarter-hour ticks are the softest, half-hour a touch stronger,
                                hour lines the strongest so the 00 boundaries anchor the eye. */}
                            {quarterMarks.map((m) => (
                                <div key={`t-${m}`} className="absolute inset-x-0 border-t border-navy/[0.06] pointer-events-none z-[1]" style={{ top: (m - minMinute) * PX_PER_MIN }} />
                            ))}
                            {halfHourMarks.map((m) => (
                                <div key={`h-${m}`} className="absolute inset-x-0 border-t border-navy/[0.09] pointer-events-none z-[1]" style={{ top: (m - minMinute) * PX_PER_MIN }} />
                            ))}
                            {hoursMarks.map((m) => (
                                <div key={m} className="absolute inset-x-0 border-t border-navy/[0.18] pointer-events-none z-[1]" style={{ top: (m - minMinute) * PX_PER_MIN }} />
                            ))}
                            {/* Past-time overlay — subtle grey wash on the elapsed portion
                                of today's column. Very light so grid lines still show. */}
                            {showNowLine(iso) && (
                                <div className="absolute inset-x-0 pointer-events-none z-[2]"
                                     style={{ top: 0, height: Math.max(0, (nowMin - minMinute) * PX_PER_MIN), backgroundColor: "rgba(15,26,61,0.05)" }}
                                     data-testid={`past-overlay-${iso}`} />
                            )}
                            {/* Out-of-hours — solid deep navy, matches mockup. Its own set
                                of white-tinted grid lines render INSIDE the overlay so ticks
                                stay legible on the dark background. z-[3] sits above base
                                grid + past overlay but below appointments (z-10+). */}
                            {info.dayStart == null ? (
                                <div className="absolute inset-0 flex items-center justify-center text-[11px] uppercase tracking-[0.24em] text-white/75 font-semibold pointer-events-none z-[3] overflow-hidden"
                                     style={{ backgroundColor: "#111C42" }}
                                     data-testid={`closed-${iso}`}>
                                    {/* Inner grid on navy */}
                                    {quarterMarks.map((m) => (
                                        <div key={`c-t-${m}`} className="absolute inset-x-0 border-t border-white/[0.06]" style={{ top: (m - minMinute) * PX_PER_MIN }} />
                                    ))}
                                    {halfHourMarks.map((m) => (
                                        <div key={`c-h-${m}`} className="absolute inset-x-0 border-t border-white/[0.08]" style={{ top: (m - minMinute) * PX_PER_MIN }} />
                                    ))}
                                    {hoursMarks.map((m) => (
                                        <div key={`c-${m}`} className="absolute inset-x-0 border-t border-white/[0.14]" style={{ top: (m - minMinute) * PX_PER_MIN }} />
                                    ))}
                                    <span className="relative">Closed</span>
                                </div>
                            ) : (
                                <>
                                    {info.dayStart > minMinute && (
                                        <div className="absolute inset-x-0 pointer-events-none z-[3] overflow-hidden flex items-center justify-center"
                                             style={{ top: 0, height: (info.dayStart - minMinute) * PX_PER_MIN, backgroundColor: "#111C42" }}
                                             data-testid={`ooh-pre-${iso}`}>
                                            {quarterMarks.filter((m) => m < info.dayStart).map((m) => (
                                                <div key={`op-t-${m}`} className="absolute inset-x-0 border-t border-white/[0.06]" style={{ top: (m - minMinute) * PX_PER_MIN }} />
                                            ))}
                                            {halfHourMarks.filter((m) => m < info.dayStart).map((m) => (
                                                <div key={`op-h-${m}`} className="absolute inset-x-0 border-t border-white/[0.08]" style={{ top: (m - minMinute) * PX_PER_MIN }} />
                                            ))}
                                            {hoursMarks.filter((m) => m < info.dayStart).map((m) => (
                                                <div key={`op-hr-${m}`} className="absolute inset-x-0 border-t border-white/[0.14]" style={{ top: (m - minMinute) * PX_PER_MIN }} />
                                            ))}
                                            {(info.dayStart - minMinute) >= 60 && (
                                                <div className="relative flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold text-white/70">
                                                    <Moon className="h-3.5 w-3.5" strokeWidth={1.75} />
                                                    Out of hours
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {info.dayEnd < minMinute + totalMinutes && (
                                        <div className="absolute inset-x-0 pointer-events-none z-[3] overflow-hidden flex items-center justify-center"
                                             style={{ top: (info.dayEnd - minMinute) * PX_PER_MIN, height: (minMinute + totalMinutes - info.dayEnd) * PX_PER_MIN, backgroundColor: "#111C42" }}
                                             data-testid={`ooh-post-${iso}`}>
                                            {quarterMarks.filter((m) => m >= info.dayEnd).map((m) => (
                                                <div key={`oa-t-${m}`} className="absolute inset-x-0 border-t border-white/[0.06]" style={{ top: (m - info.dayEnd) * PX_PER_MIN }} />
                                            ))}
                                            {halfHourMarks.filter((m) => m >= info.dayEnd).map((m) => (
                                                <div key={`oa-h-${m}`} className="absolute inset-x-0 border-t border-white/[0.08]" style={{ top: (m - info.dayEnd) * PX_PER_MIN }} />
                                            ))}
                                            {hoursMarks.filter((m) => m >= info.dayEnd).map((m) => (
                                                <div key={`oa-hr-${m}`} className="absolute inset-x-0 border-t border-white/[0.14]" style={{ top: (m - info.dayEnd) * PX_PER_MIN }} />
                                            ))}
                                            {(minMinute + totalMinutes - info.dayEnd) >= 60 && (
                                                <div className="relative flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold text-white/70">
                                                    <Moon className="h-3.5 w-3.5" strokeWidth={1.75} />
                                                    Out of hours
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                            {/* Break bands (implicit gap + explicit timeOutList) */}
                            {info.breaks.map((b, i) => (
                                <div key={`br-${i}`} className="absolute inset-x-1 z-[4] rounded bg-navy/70 border border-dashed border-gold/40 text-[10px] uppercase tracking-[0.16em] text-gold font-semibold flex items-center justify-center cursor-grab"
                                     role="button" tabIndex={0} aria-label="Move break" title="Drag or select to move this break"
                                     data-appt-block data-appt-movable="true"
                                     data-appt-payload={JSON.stringify({ calendarBreak: true, id: `break-${iso}-${i}`, bookingStart: `${iso} ${minToHHMM(b.start)}:00`, bookingEnd: `${iso} ${minToHHMM(b.end)}:00` })}
                                     draggable onDragStart={(e) => {
                                         // Keep the touch long-press in control, just as for appointments.
                                         // Native dragging can cancel touchend and lose the confirmation.
                                         if (gestureRef.current.state.startsWith("appt-")) { e.preventDefault(); return; }
                                         e.stopPropagation();
                                         e.dataTransfer.effectAllowed = "move";
                                         e.dataTransfer.setData("application/json", e.currentTarget.dataset.apptPayload);
                                     }}
                                     onDragEnd={() => { setDropHover(null); suppressedClickRef.current = { until: Date.now() + 800 }; }}
                                     onClick={() => { if (gestureRef.current.state === "idle" && Date.now() >= (suppressedClickRef.current?.until || 0)) onRescheduleAppt?.({ calendarBreak: true, bookingStart: `${iso} ${minToHHMM(b.start)}:00`, bookingEnd: `${iso} ${minToHHMM(b.end)}:00` }, iso, b.start); }}
                                     onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onRescheduleAppt?.({ calendarBreak: true, bookingStart: `${iso} ${minToHHMM(b.start)}:00`, bookingEnd: `${iso} ${minToHHMM(b.end)}:00` }, iso, b.start); } }}
                                     data-testid={`break-${iso}-${i}`}
                                     style={{ top: (b.start - minMinute) * PX_PER_MIN, height: (b.end - b.start) * PX_PER_MIN }}>
                                    {b.kind === "timeout" ? "Break" : "Lunch"}
                                </div>
                            ))}
                            {/* Past-slot shade — dims everything before "now" on today so the
                                area reads as unavailable. pointer-events-none keeps existing
                                appointments in the past region still clickable; the gesture
                                handlers themselves reject clicks/drags that begin on this shade. */}
                            {iso === londonToday && nowMin > minMinute && (
                                <div
                                    className="absolute inset-x-0 bg-black/50 pointer-events-none"
                                    data-testid={`past-shade-${iso}`}
                                    style={{ top: 0, height: (Math.min(nowMin, minMinute + totalMinutes) - minMinute) * PX_PER_MIN }}
                                />
                            )}
                            {/* Appointments */}
                            {(() => {
                                // Find the "next upcoming" appointment for today only.
                                // Used to decorate that ONE card with the Ready-Early quick action
                                // without overcrowding the rest of the calendar.
                                const now = new Date();
                                let nextReadyId = null;
                                if (iso === londonToday) {
                                    const future = items.filter(a => {
                                        const st = (a.status || "").toLowerCase();
                                        if (["canceled","cancelled","rejected","no-show","noshow","no_show","checked-out"].includes(st)) return false;
                                        try {
                                            const d = new Date((a.bookingStart || "").replace(" ", "T"));
                                            return d > now;
                                        } catch { return false; }
                                    }).sort((x, y) => (x.bookingStart || "").localeCompare(y.bookingStart || ""));
                                    if (future.length) nextReadyId = future[0].id;
                                }
                                return items.map((a) => {
                                const s = apptStartMin(a), e = apptEndMin(a);
                                if (s == null) return null;
                                const st = (a.status || "").toLowerCase();
                                const outsideHours = info.dayStart == null || s < info.dayStart || e > info.dayEnd;
                                const categoryColour = a.calendarColours?.[0]?.colour;
                                const cardColour = categoryColour || (outsideHours ? '#ffffff' : '#13213f');
                                const ink = readableText(cardColour);
                                const tone = outsideHours ? "bg-white/90 border-gold text-navy" : (STATUS_TONE[st] || "bg-white/10 border-white/20 text-white");
                                const paid = ((a.payment || {}).status || "").toLowerCase() === "paid";
                                const paidAmount = Number((a.payment || {}).amount || 0);
                                const outstanding = Math.max(Number(a.price || 0) - paidAmount, 0);
                                const isReturning = returningCustomerIds?.has?.(a.customerId) || false;
                                const formsDone = formsCompletedApptIds?.has?.(a.id) || false;
                                const durMin = e - s;
                                const heightPx = Math.max(durMin * PX_PER_MIN - 2, 22);
                                const isTiny = durMin <= 10;
                                const isShort = durMin < 25;
                                const service = a.serviceName || `#${a.serviceId}`;
                                const customer = `${a.customer.firstName || ""} ${a.customer.lastName || ""}`.trim() || "—";
                                return (
                                    <Link key={a.id} to={`/staff/appointments/${a.id}`}
                                        data-appt-block
                                        data-appt-id={a.id}
                                        data-appt-movable={!!onRescheduleAppt && !["canceled","cancelled","rejected","no-show"].includes(st)}
                                        onContextMenu={(evt) => { evt.preventDefault(); }}
                                        data-appt-payload={JSON.stringify({
                                            id: a.id,
                                            bookingStart: a.bookingStart,
                                            bookingEnd: a.bookingEnd,
                                            serviceName: a.serviceName,
                                            customer: a.customer,
                                            price: a.price,
                                        })}
                                        data-testid={`appt-${a.id}`}
                                        title={`${service} · ${customer} · ${minToHHMM(s)} · ${st} · ${(a.calendarColours || []).map(c=>c.name).join(", ")} — drag to reschedule`}
                                        draggable={!!onRescheduleAppt && !["canceled","cancelled","rejected","no-show"].includes(st)}
                                        onDragStart={(e) => {
                                            // Native HTML dragging must not steal a mobile long press.
                                            if (gestureRef.current.state.startsWith("appt-")) {
                                                e.preventDefault();
                                                return;
                                            }
                                            // Ship a minimal payload — the drop handler needs id, current start
                                            // and customer meta to build the confirmation dialog copy.
                                            e.dataTransfer.effectAllowed = "move";
                                            const payload = {
                                                id: a.id,
                                                bookingStart: a.bookingStart,
                                                bookingEnd: a.bookingEnd,
                                                serviceName: a.serviceName,
                                                customer: a.customer,
                                                price: a.price,
                                            };
                                            e.dataTransfer.setData("application/json", JSON.stringify(payload));
                                        }}
                                        onClickCapture={(e) => {
                                            const suppressed = suppressedClickRef.current;
                                            if (suppressed?.id === a.id && Date.now() <= suppressed.until) {
                                                e.preventDefault(); e.stopPropagation();
                                                suppressedClickRef.current = null;
                                            }
                                        }}
                                        className={`absolute inset-x-1 rounded-sm border-l-2 border-y border-r shadow-sm overflow-hidden ${tone} ${onRescheduleAppt ? "cursor-grab active:cursor-grabbing" : ""}`}
                                        style={{
                                            top: ((dropHover?.apptId === a.id ? dropHover.min : s) - minMinute) * PX_PER_MIN,
                                            height: heightPx,
                                            zIndex: dropHover?.apptId === a.id ? 30 : 10,
                                            backgroundColor:cardColour, color:ink,
                                            touchAction: "manipulation",
                                            WebkitTouchCallout: "none",
                                            WebkitUserSelect: "none",
                                            userSelect: "none",
                                        }}>
                                        <div className={`h-full flex items-center gap-1.5 ${isTiny ? "px-1.5 py-0" : "px-2 py-1"}`}>
                                            {a.calendarColours?.length>1 && <div className="flex flex-col h-full w-1 shrink-0" aria-label={a.calendarColours.map(c=>c.name).join(', ')}>{a.calendarColours.map(c=><span key={c.name} title={c.name} style={{backgroundColor:c.colour,flex:1,border:'1px solid '+readableText(c.colour)}}/>)}</div>}
                                            <div className="min-w-0 flex-1 flex flex-col justify-center">
                                                <div style={{color:ink}} className={`font-ui leading-tight truncate  ${isTiny ? "text-[10px]" : "text-[11px]"}`}>
                                                    {service}
                                                </div>
                                                {!isTiny && (
                                                    <div style={{color:ink}} className={`text-[10px]  leading-tight truncate`}>
                                                        {customer}{isShort ? "" : ` · ${minToHHMM(s)}`}
                                                    </div>
                                                )}
                                                {!isShort && (
                                                    <div style={{color:ink}} className="text-[9px] leading-tight mt-0.5">{minToHHMM(s)}</div>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-0.5 shrink-0 rounded bg-[#0b142b] p-0.5" data-testid={`appt-badges-${a.id}`}>
                                                {st === "approved" && (
                                                    <span title="Approved" data-testid={`badge-approved-${a.id}`}
                                                          className="inline-flex items-center rounded-full border border-emerald-400/40 bg-emerald-500/15 text-emerald-100 h-3.5 w-3.5 justify-center">
                                                        <CheckCircle2 className="h-2.5 w-2.5" strokeWidth={2.5} />
                                                    </span>
                                                )}
                                                {paid && (
                                                    <span title="Deposit paid" data-testid={`badge-paid-${a.id}`}
                                                          className="inline-flex items-center rounded-full border border-gold/50 bg-gold/15 text-gold h-3.5 w-3.5 justify-center">
                                                        <CreditCard className="h-2.5 w-2.5" strokeWidth={2.5} />
                                                    </span>
                                                )}
                                                {formsDone && (
                                                    <span title="Forms completed" data-testid={`badge-forms-${a.id}`}
                                                          className="inline-flex items-center rounded-full border border-blue-400/50 bg-blue-500/15 text-blue-100 h-3.5 w-3.5 justify-center">
                                                        <FileText className="h-2.5 w-2.5" strokeWidth={2.5} />
                                                    </span>
                                                )}
                                                {isReturning && (
                                                    <span title="Returning customer" data-testid={`badge-returning-${a.id}`}
                                                          className="inline-flex items-center rounded-full border border-purple-400/50 bg-purple-500/15 text-purple-100 h-3.5 w-3.5 justify-center">
                                                        <Star className="h-2.5 w-2.5" strokeWidth={2.5} />
                                                    </span>
                                                )}
                                                {outstanding > 0 && st !== "canceled" && st !== "cancelled" && (
                                                    <span title="Outstanding balance" data-testid={`badge-outstanding-${a.id}`}
                                                          className="inline-flex items-center rounded-full border border-amber-400/50 bg-amber-500/15 text-amber-100 h-3.5 w-3.5 justify-center">
                                                        <AlertTriangle className="h-2.5 w-2.5" strokeWidth={2.5} />
                                                    </span>
                                                )}
                                                {a.id === nextReadyId && !isTiny && (
                                                    <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                                                        <ReadyEarlyButton appointment={a} variant="calendar" />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </Link>
                                );
                            });
                            })()}
                            {/* Live drag highlight — always shows start–end + duration */}
                            {drag && drag.iso === iso && (() => {
                                const durMin = Math.max(drag.endMin - drag.startMin, SNAP_MIN);
                                const topPx = (drag.startMin - minMinute) * PX_PER_MIN;
                                const heightPx = durMin * PX_PER_MIN;
                                const isTiny = heightPx < 44;
                                return (
                                    <div className="absolute inset-x-1 z-20 pointer-events-none" data-testid={`drag-preview-${iso}`}>
                                        <div className="absolute inset-x-0 rounded-md bg-gold/25 border-2 border-gold shadow-[0_0_0_2px_rgba(197,160,89,0.15)]"
                                             style={{ top: topPx, height: heightPx }} />
                                        {/* Floating time chip — sits above the block when tiny, inside otherwise */}
                                        <div
                                            className={`absolute rounded-full bg-gold text-navy px-2.5 py-1 text-[11px] font-semibold shadow-[0_6px_16px_rgba(0,0,0,0.35)] flex items-center gap-1 whitespace-nowrap ${isTiny ? "" : "left-1/2 -translate-x-1/2"}`}
                                            style={isTiny
                                                ? { top: Math.max(topPx - 26, 0), left: 4 }
                                                : { top: topPx + Math.max(4, heightPx / 2 - 12), left: "50%" }}
                                        >
                                            <Plus className="h-3 w-3" strokeWidth={2.5} />
                                            {minToHHMM(drag.startMin)} → {minToHHMM(drag.endMin)} · {durMin} min
                                        </div>
                                    </div>
                                );
                            })()}
                            {/* Now line — gold, thin, dot on the left. The time label in
                                the gutter is added separately (see gutter render) so it
                                appears aligned with the row divider. */}
                            {showNowLine(iso) && (
                                <div className="absolute inset-x-0 z-10 pointer-events-none" style={{ top: (nowMin - minMinute) * PX_PER_MIN }} data-testid={`now-line-${iso}`}>
                                    <div className="h-px bg-gold" />
                                    <div className="absolute -left-1.5 -top-1 h-2.5 w-2.5 rounded-full bg-gold shadow" />
                                </div>
                            )}
                            {/* Drop-target indicator during drag-to-reschedule */}
                            {dropHover && dropHover.iso === iso && (
                                <div className="absolute inset-x-0 z-20 pointer-events-none" data-testid={`drop-hover-${iso}`}
                                     style={{ top: (dropHover.min - minMinute) * PX_PER_MIN }}>
                                    <div className="h-0.5 bg-gold shadow-[0_0_8px_rgba(197,160,89,0.6)]" />
                                    <div className="absolute -top-3 left-1 rounded-full bg-gold text-navy px-2 py-0.5 text-[10px] font-semibold shadow">
                                        {minToHHMM(dropHover.min)}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ==========================================================================
 * MonthView — closed iff live weekDayList has no periods OR dayOff covers it
 * ======================================================================= */
function MonthView({ anchor, byDay, infoForDate, onPickDay }) {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const startCol = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
    const todayISO = toISODate(new Date());
    const cells = [];
    for (let i = 0; i < startCol; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(anchor.getFullYear(), anchor.getMonth(), d));
    return (
        <div data-testid="month-view">
            <div className="grid grid-cols-7 gap-1 md:gap-2 text-[10px] uppercase tracking-[0.16em] text-white/40 mb-1">
                {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => <div key={d} className="px-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1 md:gap-2">
                {cells.map((d, i) => {
                    if (!d) return <div key={i} />;
                    const key = toISODate(d);
                    const items = byDay[key] || [];
                    const info = infoForDate(d);
                    const closed = info.periods.length === 0;
                    const isToday = key === todayISO;
                    return (
                        <button key={key} onClick={() => onPickDay(key)} data-testid={`month-day-${key}`}
                            className={`min-h-16 md:min-h-20 rounded-lg border p-1.5 text-left transition-colors ${isToday ? "border-gold bg-gold/5" : closed ? "border-white/5 bg-black/20 text-white/40" : "hairline bg-white/[0.02] hover:border-gold/50"}`}>
                            <div className={`text-xs ${isToday ? "text-gold font-semibold" : "text-white/80"}`}>{d.getDate()}</div>
                            <div className="flex flex-wrap gap-1 mt-1">{[...new Map(items.flatMap(a=>a.calendarColours || []).map(c=>[c.name,c])).values()].map(c=><span key={c.name} title={c.name} aria-label={c.name} style={{backgroundColor:c.colour,border:'1px solid white',width:10,height:10,borderRadius:10}}/>)}</div>
                            {closed && <div className="mt-1 text-[9px] uppercase tracking-[0.14em] text-white/30" data-testid={`month-closed-${key}`}>Closed</div>}
                            {!closed && items.length > 0 && <div className="mt-1 text-[10px] text-gold">{items.length} appt{items.length===1?"":"s"}</div>}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/* ==========================================================================
 * ConfirmBreakDialog — staff-only override for break/closed selections
 * ======================================================================= */
function ConfirmBreakDialog({ state, onCancel, onConfirm }) {
    const { iso, startMin, endMin, kind } = state;
    const dateText = new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
    const isBreak = kind === "break";
    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="cal-confirm-modal" onClick={onCancel}>
            <div className="w-full sm:max-w-md m-4 rounded-2xl glass-strong border hairline p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start gap-3">
                    <div className="min-h-9 min-w-9 rounded-full bg-amber-500/15 border border-amber-400/40 flex items-center justify-center text-amber-200">
                        <AlertTriangle className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                        <h3 className="font-serif text-white text-xl">{isBreak ? "This falls inside your scheduled break." : "This falls outside your working hours."}</h3>
                        <p className="text-xs text-white/60 mt-1">{dateText} · {minToHHMM(startMin)}–{minToHHMM(endMin)}</p>
                        <p className="text-sm text-white/70 mt-3">
                            Customers cannot book here. As the practitioner you can override and create the appointment manually.
                        </p>
                    </div>
                </div>
                <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2">
                    <button onClick={onCancel} data-testid="cal-confirm-cancel" className="h-11 flex-1 rounded-full border hairline text-white/80 text-sm hover:border-gold/50">Cancel</button>
                    <button onClick={onConfirm} data-testid="cal-confirm-accept" className="h-11 flex-1 rounded-full bg-gold text-navy font-semibold text-sm">Continue anyway</button>
                </div>
            </div>
        </div>
    );
}

export { DAY_NAMES };

/* ==========================================================================
 * RangeChoiceModal — shown when the user drag-selects an available range so
 * they can consciously pick between creating a booking or blocking the slot.
 * Prevents accidental double-booking when Angela meant to carve out a break.
 * ======================================================================= */
function RangeChoiceModal({ state, onCancel, onBook, onBlock }) {
    const { iso, startMin, endMin } = state;
    const dur = Math.max(endMin - startMin, 5);
    const day = new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
             data-testid="cal-range-choice"
             onClick={onCancel}>
            <div className="w-full sm:max-w-md m-4 rounded-2xl glass-strong border hairline p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
                <div className="min-w-0">
                    <h3 className="font-serif text-white text-xl">What do you want to do?</h3>
                    <p className="text-sm text-white/70 mt-1">
                        {day} · {minToHHMM(startMin)}–{minToHHMM(endMin)} · {dur} min
                    </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button onClick={onBook} data-testid="cal-range-choice-book"
                            className="min-h-14 rounded-2xl bg-gold text-navy font-semibold text-sm inline-flex items-center justify-center gap-2 px-4">
                        <Plus className="h-4 w-4" strokeWidth={2.5} />
                        New booking
                    </button>
                    <button onClick={onBlock} data-testid="cal-range-choice-block"
                            className="min-h-14 rounded-2xl border border-white/40 text-white font-semibold text-sm hover:border-gold hover:text-gold inline-flex items-center justify-center gap-2 px-4">
                        <Ban className="h-4 w-4" strokeWidth={2.25} />
                        Block this time
                    </button>
                </div>
                <button onClick={onCancel} data-testid="cal-range-choice-cancel"
                        className="w-full h-10 rounded-full text-xs text-white/60 hover:text-white">
                    Cancel
                </button>
            </div>
        </div>
    );
}

/* ==========================================================================
 * RescheduleConfirmDialog — reviewed after every drag-to-reschedule drop so
 * Angela never accidentally moves a customer without confirming.
 * ======================================================================= */
function RescheduleConfirmDialog({ state, onCancel, onConfirm }) {
    const { appt, newIso, newStartMin } = state;
    const oldIso = (appt.bookingStart || "").slice(0, 10);
    const oldStart = apptStartMin(appt);
    const oldEnd = apptEndMin(appt);
    const durMin = Math.max(oldEnd - oldStart, 15);
    const newEnd = newStartMin + durMin;
    const svc = appt.serviceName || `#${appt.serviceId}`;
    const customer = `${appt.customer?.firstName || ""} ${appt.customer?.lastName || ""}`.trim() || "the customer";
    const fmtDay = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
             data-testid="cal-reschedule-modal"
             onClick={onCancel}>
            <div className="w-full sm:max-w-md m-4 rounded-2xl glass-strong border hairline p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start gap-3">
                    <div className="min-h-9 min-w-9 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center text-gold">
                        <CalendarDays className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h3 className="font-serif text-white text-xl">Reschedule appointment?</h3>
                        <p className="text-sm text-white/70 mt-1">
                            {customer} — {svc}
                        </p>
                        <div className="mt-3 space-y-2 text-sm">
                            <div className="flex items-center justify-between rounded-lg bg-white/[0.04] px-3 py-2">
                                <span className="text-white/60 text-xs uppercase tracking-[0.16em]">Currently</span>
                                <span className="text-white/90 font-medium">{fmtDay(oldIso)} · {minToHHMM(oldStart)}–{minToHHMM(oldEnd)}</span>
                            </div>
                            <div className="flex items-center justify-between rounded-lg bg-gold/10 border border-gold/40 px-3 py-2">
                                <span className="text-gold text-xs uppercase tracking-[0.16em] font-semibold">Move to</span>
                                <span className="text-gold font-semibold">{fmtDay(newIso)} · {minToHHMM(newStartMin)}–{minToHHMM(newEnd)}</span>
                            </div>
                        </div>
                        <p className="mt-3 text-[11px] text-white/55">
                            The customer will receive an automatic e-mail confirming the change and their reminder will be updated.
                        </p>
                    </div>
                </div>
                <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2">
                    <button onClick={onCancel} data-testid="cal-reschedule-cancel"
                            className="h-11 min-h-11 shrink-0 sm:flex-1 rounded-full border hairline text-white/80 text-sm hover:border-gold/50">
                        Cancel
                    </button>
                    <button onClick={onConfirm} data-testid="cal-reschedule-confirm"
                            className="h-11 min-h-11 shrink-0 sm:flex-1 rounded-full bg-gold text-navy font-semibold text-sm">
                        Reschedule & notify
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ==========================================================================
 * BlockTimeModal — quick "Block time" gesture for holidays / breaks without
 * jumping to the Availability settings page. Whole-day blocks POST a day-off;
 * time-range blocks POST a special-day whose periodList excludes the block.
 * ======================================================================= */
export function BlockTimeModal({ anchor, employeeId, employee, infoForDate, initialRange, onClose, onSaved }) {
    const prefilled = !!initialRange;
    const moving = !!initialRange?.originalStart;
    const breakDuration = moving ? hhmmToMin(initialRange.originalEnd) - hhmmToMin(initialRange.originalStart) : 0;
    const [mode, setMode] = useState("range"); // "range" | "whole-day"
    const [date, setDate] = useState(initialRange?.iso || toISODate(anchor));
    const [startTime, setStartTime] = useState(initialRange ? minToHHMM(initialRange.startMin) : "13:00");
    const [endTime, setEndTime] = useState(initialRange ? minToHHMM(initialRange.endMin) : "14:00");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [snapshot, setSnapshot] = useState(null);
    const [loadingSnapshot, setLoadingSnapshot] = useState(true);
    const snapshotRequest = useRef(0);
    const loadSnapshot = async () => {
        const request = ++snapshotRequest.current;
        setLoadingSnapshot(true); setSnapshot(null); setError(null);
        try {
            // The calendar uses cached entities. Save validation uses the raw
            // provider record, so obtain the same representation before editing.
            const response = await staffApi.availGetEmployee(employeeId);
            const raw = response?.employee;
            if (!raw || String(raw.id) !== String(employeeId)) throw new Error("Could not load current availability. Please retry.");
            if (moving) {
                const specials = (response.specialDays || raw.specialDayList || []).filter(s => String(s.startDate).slice(0,10) <= date && String(s.endDate).slice(0,10) >= date);
                const weekday = new Date(`${date}T12:00:00`).getDay() || 7;
                const source = specials[0] || (raw.weekDayList || []).find(w => Number(w.dayIndex) === weekday);
                const stillExists = normaliseWeekDay(source).breaks.some(b => b.start === hhmmToMin(initialRange.originalStart) && b.end === hhmmToMin(initialRange.originalEnd));
                if (!stillExists) throw new Error("This break has changed. Close this dialog and refresh the calendar before moving it.");
            }
            if (request === snapshotRequest.current) setSnapshot(raw);
        } catch (e) { if (request === snapshotRequest.current) setError(e.message || "Could not load current availability."); }
        finally { if (request === snapshotRequest.current) setLoadingSnapshot(false); }
    };
    useEffect(() => {
        loadSnapshot();
        const overflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { snapshotRequest.current++; document.body.style.overflow = overflow; };
        // A dialog captures one fresh provider snapshot, not the cached entities.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeId]);
    const editStart = (value) => {
        setStartTime(value);
        if (moving && value) {
            const end = hhmmToMin(value) + breakDuration;
            setEndTime(end < 1440 ? minToHHMM(end) : "");
        }
    };
    const editEnd = (value) => {
        setEndTime(value);
        if (moving && value) {
            const start = hhmmToMin(value) - breakDuration;
            setStartTime(start >= 0 ? minToHHMM(start) : "");
        }
    };

    // Pre-fill sensible defaults from the day's working window when there's no
    // dragged-in range (`prefilled` is false).
    useEffect(() => {
        if (prefilled) return;
        try {
            const info = infoForDate(new Date(`${date}T00:00:00`));
            if (info?.dayStart != null && info?.dayEnd != null) {
                setStartTime(minToHHMM(Math.max(info.dayStart, 13 * 60)));
                setEndTime(minToHHMM(Math.min(info.dayEnd, 14 * 60)));
            }
        } catch (_) { /* keep defaults */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    const submit = async () => {
        if (saving || loadingSnapshot || !snapshot) return;
        setError(null);
        if (!employeeId) {
            setError("Availability isn't loaded yet — please retry in a moment.");
            return;
        }
        setSaving(true);
        try {
            if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || (mode === "range" && (!/^\d{2}:\d{2}$/.test(startTime) || !/^\d{2}:\d{2}$/.test(endTime)))) throw new Error("Choose a valid date and both times.");
            if (mode === "range" && endTime <= startTime) throw new Error("End time must be after start time.");
            if (mode === "whole-day") {
                const response = await staffApi.availAddDayOff(employeeId, {
                    name: reason || "Blocked",
                    startDate: date,
                    endDate: date,
                    repeat: false,
                }, false);
                if (response.requires_confirmation) throw new Error("This day has appointments. Reschedule them before blocking the whole day.");
            } else {
                await staffApi.calendarBlock(employeeId, {
                    date, startTime, endTime,
                    originalStart: initialRange?.originalStart || null,
                    originalEnd: initialRange?.originalEnd || null,
                    expected: { weekDayList: snapshot.weekDayList || [], specialDayList: snapshot.specialDayList || [], dayOffList: snapshot.dayOffList || [] },
                });
            }
            onSaved?.();
        } catch (e) {
            setError(e?.message || "Could not save the block.");
        } finally {
            setSaving(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
             data-testid="cal-block-modal" role="dialog" aria-modal="true" aria-label={moving ? "Confirm break move" : "Block time"}
             onClick={() => { if (!saving) onClose?.(); }}>
            <div className="w-full sm:max-w-md rounded-2xl glass-strong border hairline p-5 space-y-4 max-h-[calc(100vh-2rem)] overflow-y-auto"
                 style={{ maxHeight: "calc(100dvh - 2rem)", overscrollBehavior: "contain" }}
                 onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start gap-3">
                    <div className="min-h-9 min-w-9 rounded-full bg-amber-500/15 border border-amber-400/40 flex items-center justify-center text-amber-200">
                        <Ban className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h3 className="font-serif text-white text-xl">{moving ? "Move this break?" : "Block time"}</h3>
                        <p className="text-xs text-white/60 mt-1">
                            Existing appointments and other breaks are protected. Changes apply to this date only.
                        </p>
                    </div>
                </div>

                {moving && <div className="rounded-lg border border-gold/40 bg-gold/10 p-3 space-y-2 text-sm text-white" data-testid="break-move-summary">
                    <p>Currently: <strong>{initialRange.originalStart}–{initialRange.originalEnd}</strong></p>
                    <p>Move to: <strong>{startTime || "Choose start"}–{endTime || "Choose end"}</strong></p>
                    <p className="text-xs text-white/70">Adjust either time below if needed. The break stays {breakDuration} minutes long. Nothing changes until you confirm.</p>
                </div>}

                {!initialRange?.originalStart && <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => setMode("range")}
                            data-testid="block-mode-range"
                            className={`h-10 rounded-full text-xs font-semibold border transition-colors ${mode === "range" ? "bg-gold text-navy border-gold" : "text-white border-white/30 hover:border-gold/60"}`}>
                        Time range
                    </button>
                    <button onClick={() => setMode("whole-day")}
                            data-testid="block-mode-whole"
                            className={`h-10 rounded-full text-xs font-semibold border transition-colors ${mode === "whole-day" ? "bg-gold text-navy border-gold" : "text-white border-white/30 hover:border-gold/60"}`}>
                        Whole day
                    </button>
                </div>}

                <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-semibold">Date</span>
                    <input type="date" value={date} disabled={moving || saving} onChange={(e) => setDate(e.target.value)}
                           data-testid="block-date"
                           className="mt-1.5 w-full h-11 bg-transparent border hairline rounded-md px-3 text-white text-sm" />
                </label>

                {mode === "range" && (
                    <div className="grid grid-cols-2 gap-2">
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-semibold">Start</span>
                            <StaffTimePicker label="Start time" value={startTime} disabled={saving} onChange={editStart}
                                   data-testid="block-start"
                                   className="mt-1.5 w-full h-11 bg-transparent border hairline rounded-md px-3 text-white text-sm" />
                        </label>
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-semibold">End</span>
                            <StaffTimePicker label="End time" value={endTime} disabled={saving} onChange={editEnd}
                                   data-testid="block-end"
                                   className="mt-1.5 w-full h-11 bg-transparent border hairline rounded-md px-3 text-white text-sm" />
                        </label>
                    </div>
                )}

                <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-semibold">{mode === "whole-day" ? "Reason (optional)" : ""}</span>
                    <input value={reason} onChange={(e) => setReason(e.target.value)}
                           data-testid="block-reason" hidden={mode !== "whole-day"}
                           placeholder="Lunch, holiday, admin..."
                           className="mt-1.5 w-full h-11 bg-transparent border hairline rounded-md px-3 text-white text-sm" />
                </label>

                {error && (
                    <div role="alert" className="rounded-lg border border-red-400/40 bg-red-500/10 text-red-100 text-xs px-3 py-2" data-testid="block-error">
                        {error}
                        <button type="button" disabled={saving || loadingSnapshot} onClick={loadSnapshot} className="block underline mt-2 min-h-11">Reload availability</button>
                    </div>
                )}

                {loadingSnapshot && <p role="status" className="text-xs text-white/70">Checking current availability…</p>}

                <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
                    <button onClick={onClose} disabled={saving}
                            className="min-h-12 h-12 flex-1 rounded-full border hairline text-white/85 text-sm font-medium hover:border-gold/50 inline-flex items-center justify-center">
                        Cancel
                    </button>
                    <button onClick={submit} disabled={saving || loadingSnapshot || !snapshot} data-testid="block-submit"
                            className="min-h-12 h-12 flex-1 rounded-full bg-gold text-navy font-semibold text-sm disabled:opacity-40 inline-flex items-center justify-center px-4 leading-none">
                        {saving ? "Saving…" : moving ? "Confirm move" : "Block time"}
                    </button>
                </div>
            </div>
        </div>, document.body
    );
}
